import React from "react";
import { Tag, Tooltip, Progress } from "antd";
import dayjs from "dayjs";

/**
 * Định nghĩa cột bảng "Bản OTA đang chạy". Tách khỏi panel vì panel đã chạm trần
 * 200 dòng và phần cột là khối thuần hiển thị, không dính state.
 */

const PLATFORM_LABEL = { ios: "iOS", android: "Android" };

const pct = (part, total) => (total > 0 ? Math.round((part / total) * 100) : 0);
const vn = (n) => Number(n || 0).toLocaleString("vi-VN");

/** Nhãn loại bundle. `bundleId = null` là nhóm quan trọng nhất: app CŨ chưa gửi header. */
function bundleKind(row) {
    if (row.bundleId == null) return { text: "Bản cũ (chưa báo)", color: "default" };
    return row.isOta
        ? { text: "OTA", color: "green" }
        : { text: "Bản gốc trong app", color: "blue" };
}

/** Ghi chú dev gõ lúc đẩy OTA. Bỏ tiền tố conventional-commit cho người không rành code. */
function humanizeMessage(message) {
    if (!message) return null;
    // "fix(saved-products): badge ..." → "badge ..."
    const stripped = message.replace(/^[a-z]+(\([^)]*\))?:\s*/i, "");
    return stripped.charAt(0).toUpperCase() + stripped.slice(1);
}

export default function buildOtaColumns(totals) {
    return [
        {
            title: (
                <Tooltip title="Mã lô bản vá máy đó đang chạy — cùng mã là cùng một bản code">
                    <span className="border-b border-dotted border-current cursor-help">Bundle</span>
                </Tooltip>
            ),
            dataIndex: "bundleId",
            width: 110,
            render: (v) =>
                v ? (
                    // 8 ký tự đầu là đủ để đối chiếu với `npx hot-updater bundle list`
                    // và với nhãn hiện trên màn Tài khoản của app.
                    <Tooltip title={v}>
                        <code className="text-xs">{v.slice(0, 8)}</code>
                    </Tooltip>
                ) : (
                    <span className="text-content-3">—</span>
                ),
        },
        {
            title: "Bản này sửa gì",
            dataIndex: "message",
            render: (_, r) => {
                if (r.bundleId == null) {
                    return (
                        <span className="text-content-3 text-xs">
                            App cũ, chưa báo mã — chắc chắn chưa có bản vá
                        </span>
                    );
                }
                if (!r.isOta) {
                    return (
                        <span className="text-content-3 text-xs">
                            Bản gốc tải từ store, chưa nhận bản vá
                        </span>
                    );
                }
                const text = humanizeMessage(r.message);
                if (!text) {
                    return <span className="text-content-3 text-xs">Không tra được ghi chú</span>;
                }
                return (
                    <div className="flex flex-col gap-1">
                        <Tooltip title={r.message}>
                            <span className="text-xs">{text}</span>
                        </Tooltip>
                        {/* enabled=false nghĩa là bản vá đã bị gỡ: máy còn mã này là chưa
                            kịp nhận bản thay thế → admin phải thấy ngay. */}
                        {r.bundleEnabled === false && (
                            <Tag color="red" className="w-fit m-0">
                                Bản vá đã bị gỡ
                            </Tag>
                        )}
                        {r.channel && r.channel !== "production" && (
                            <Tag color="orange" className="w-fit m-0">
                                Kênh thử: {r.channel}
                            </Tag>
                        )}
                    </div>
                );
            },
        },
        {
            title: "Loại",
            width: 165,
            render: (_, r) => {
                const k = bundleKind(r);
                return <Tag color={k.color}>{k.text}</Tag>;
            },
        },
        {
            title: "Nền tảng",
            dataIndex: "platform",
            width: 100,
            render: (v) => PLATFORM_LABEL[v] || v || "—",
        },
        {
            title: "Version store",
            dataIndex: "appVersions",
            width: 130,
            render: (v) => (v || []).filter(Boolean).join(", ") || "—",
        },
        {
            title: "Số máy",
            dataIndex: "users",
            width: 180,
            render: (v) => (
                <div className="flex items-center gap-2">
                    <Progress
                        percent={pct(v, totals.users)}
                        size="small"
                        showInfo={false}
                        className="flex-1 m-0"
                    />
                    <span className="whitespace-nowrap text-xs">
                        {vn(v)} <span className="text-content-3">({pct(v, totals.users)}%)</span>
                    </span>
                </div>
            ),
        },
        {
            title: "Mở app lần cuối",
            dataIndex: "lastSeen",
            width: 160,
            render: (v) => (v ? dayjs.unix(Number(v)).format("DD/MM/YYYY HH:mm") : "—"),
        },
    ];
}
